import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { DataStorage } from '../../core/data/data-storage';
import { HttpClient } from '@angular/common/http';
import { AuthenticationService } from '../../core/auth/authentication-service';
import { getApiUrl } from '../../core/helpers/rest-utils';
import { BusinessService } from '../../core/business/business.service';
import { User } from '../../core/user/user.service';
import { BusinessInvitation } from '../../business-invitation/business-invitation';
import { SessionService } from '../../core/session.service';

@Injectable({
    providedIn: 'root'
})
export class UsersService {
    persistentStorage: DataStorage = DataStorage.persistent();
    businessUsers: User[] = [];
    constructor(private http: HttpClient, private business: BusinessService, private session: SessionService) {
        this.session.refreshState.subscribe(() => this.getAllUsers().subscribe());
        this.session.terminate.subscribe(() => this.businessUsers = []);
    }

    get activeBusinessId(): string {
        return this.business.businessId;
    }

    addUser(email: string) {
        return this.http.post(`${getApiUrl()}businesses/${this.activeBusinessId}/invitations`, { email }, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => {
            const user = this.mapBusinessInvitationToUser(res.data);
            return user;
        }));
    }

    getAllUsers(): Observable<User[]> {
        if (!this.activeBusinessId) {
            return of([]);
        }
        return forkJoin([this.getUsers(), this.getInvitedUsers()])
            .pipe(map(([users, invitedUsers]) => {
                this.businessUsers = users.concat(invitedUsers);
                return this.businessUsers;
            }));
    }

    getUsers(): Observable<User[]> {
        if (!this.activeBusinessId) {
            return of([]);
        }
        return this.http.get(`${getApiUrl()}businesses/${this.activeBusinessId}/users`, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => res.data));
    }

    getInvitedUsers(): Observable<User[]> {
        if (!this.activeBusinessId) {
            return of([]);
        }
        return this.http.get(`${getApiUrl()}businesses/${this.activeBusinessId}/invitations`, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => {
            const users = res.data.map((businessInvitation: BusinessInvitation) => {
                return this.mapBusinessInvitationToUser(businessInvitation);
            });
            return users;
        }));
    }

    resendInvite(inviteId: string) {
        return this.http.post(`${getApiUrl()}businesses/${this.activeBusinessId}/invitations/${inviteId}/resend`, {}, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => res.data));
    }

    removeUser(userId: string) {
        return this.http.delete(`${getApiUrl()}businesses/${this.activeBusinessId}/users/${userId}`, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => res.data));
    }

    removeInactiveUser(invitationId: string) {
        return this.http.delete(`${getApiUrl()}businesses/${this.activeBusinessId}/invitations/${invitationId}`, { headers: { 'X-Business-ID': this.activeBusinessId } }).pipe(map((res: any) => res.data));
    }

    private mapBusinessInvitationToUser(businessInvitation: BusinessInvitation): User {
        return {
            email: businessInvitation.email,
            inviteId: businessInvitation.token,
            lastActive: null,
            role: businessInvitation.role,
        }
    }
}

